import { useState } from "react";
import { Link } from "react-router-dom";
import { Archive, ArrowUpRight, Loader2, Pencil, RefreshCw, Save, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { CategorySettingsDialog } from "@/components/CategorySettingsDialog";

export const ProductHeader = ({ p, cat, theme, dirty, onSave, onDelete, onPatch, setIssue, onCatSaved }) => {
  const [editIssue, setEditIssue] = useState(false);
  const [editCat, setEditCat] = useState(false);
  const ready = !!p.files?.pdf_path;

  const sync = p.store_sync || {};
  const syncing = sync.status === "running";
  const togglePublish = async () => {
    if (dirty) return toast.error("Save your changes first");
    if (p.published && !window.confirm("Unpublish? The title is switched to Draft on softspinepress.com, so it can't be bought.")) return;
    try {
      const r = await api.post(`/products/${p.id}/publish`, null, { params: { published: !p.published } });
      onPatch({ published: r.data.published, published_at: r.data.published_at, store_sync: r.data.store_sync });
      toast.success(r.data.published ? "Sending to softspinepress.com…" : "Unpublished — now Draft on softspinepress.com");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resync = async () => {
    if (dirty) return toast.error("Save your changes first");
    try {
      const r = await api.post(`/products/${p.id}/shop-sync`);
      onPatch({ store_sync: r.data.store_sync });
      toast.message("Updating softspinepress.com…");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-6 md:mx-10 mt-8 border-l-4 px-6 md:px-8 py-7 rise" style={{ background: theme.tint, borderColor: theme.accent }} data-testid="product-header">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono-meta text-[11px] tracking-[0.2em] uppercase text-[#2A2325]/75">
        <span>{p.series || cat?.name}</span>
        <span>·</span>
        {editIssue ? (
          <Input autoFocus value={p.issue_no || ""} onChange={(e) => setIssue(e.target.value)} onBlur={() => setEditIssue(false)} className="h-6 w-20 text-xs bg-white" data-testid="issue-no-input" />
        ) : (
          <button onClick={() => setEditIssue(true)} className="flex items-center gap-1 hover:text-[#2A2325]" data-testid="issue-no-edit-btn">
            No. {p.issue_no || "—"} <Pencil size={10} />
          </button>
        )}
        <span>·</span>
        <span data-testid="header-edition">Edition {p.edition || 1}</span>
        <button onClick={() => setEditCat(true)} title="Change this category's colours" className="flex items-center gap-1 ml-2 normal-case tracking-normal hover:underline" data-testid="header-theme">
          <span className="w-2.5 h-2.5 inline-block" style={{ background: theme.dark }} />
          <span className="w-2.5 h-2.5 inline-block" style={{ background: theme.accent }} />
          {theme.name} <Pencil size={10} />
        </button>
      </div>
      <CategorySettingsDialog cat={cat} open={editCat} onOpenChange={setEditCat} onSaved={onCatSaved} />
      <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight" style={{ color: theme.dark }} data-testid="studio-title">{p.title}</h1>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <span className={`font-mono-meta text-[10px] tracking-[0.2em] px-2 py-1 border ${ready ? "border-[#2A2325]/30" : "border-dashed border-[#2A2325]/30"}`} data-testid="header-status">
          {p.published ? (sync.status === "failed" ? "SYNC FAILED" : syncing ? "SENDING…" : sync.store_status === "draft" ? "PUBLISHED · STORE DRAFT" : "LIVE") : ready ? "READY" : "DRAFT"}
        </span>
        {p.subtitle && <span className="font-playfair italic text-[#2A2325]/70">{p.subtitle}</span>}
      </div>
      <div className="flex flex-wrap items-start gap-3 mt-6">
        <div className="flex flex-col items-end">
          <Button variant="outline" onClick={togglePublish} className="rounded-none gap-2 bg-[#FFFDF9]" data-testid="publish-toggle-btn">
            {p.published ? <Archive size={15} /> : <Store size={15} />} {p.published ? "Unpublish from softspinepress.com" : "Publish to softspinepress.com"}
          </Button>
          <div className="text-[11px] mt-1.5 text-right max-w-[300px]" data-testid="store-sync-status">
            {syncing && <span className="flex items-center gap-1 text-[#2A2325]/70"><Loader2 size={11} className="animate-spin" /> Sending PDF & cover to softspinepress.com…</span>}
            {sync.status === "failed" && <span className="text-[#9B2C2C]" data-testid="store-sync-error">Store sync failed: {sync.error}</span>}
            {sync.status === "done" && (
              <span className="flex flex-wrap justify-end items-center gap-x-2 text-[#2D5A3F]">
                {sync.store_status === "live" ? "Live" : "Draft"} on softspinepress.com
                <a href={sync.url} target="_blank" rel="noreferrer" className="underline flex items-center gap-0.5" data-testid="store-sync-view-link">View <ArrowUpRight size={10} /></a>
              </span>
            )}
            {p.published && !syncing && (
              <button onClick={resync} className="flex items-center gap-1 ml-auto mt-1 underline text-[#2A2325]/80" data-testid="store-sync-update-btn"><RefreshCw size={10} /> {sync.status === "failed" ? "Try again" : "Update PDF & details on the store"}</button>
            )}
          </div>
          <Link to="/store" className="text-[11px] text-[#2A2325]/60 mt-1.5 hover:underline flex items-center gap-0.5" data-testid="store-connection-link">Set up the store connection <ArrowUpRight size={10} /></Link>
          <a href="/whats-new" target="_blank" rel="noreferrer" className="text-[11px] text-[#2A2325]/80 mt-1 hover:underline flex items-center gap-0.5" data-testid="whats-new-link">Public “what's new” page <ArrowUpRight size={10} /></a>
        </div>
        <Button variant="outline" onClick={onDelete} className="rounded-none gap-2 bg-[#FFFDF9]" data-testid="studio-delete-btn"><Trash2 size={15} /> Delete</Button>
        <Button onClick={onSave} disabled={!dirty} className="rounded-none gap-2 ml-auto" style={{ background: theme.dark }} data-testid="studio-save-btn"><Save size={15} /> {dirty ? "Save changes" : "Saved"}</Button>
      </div>
    </div>
  );
};
